import { DateTimeStamp } from '@/types/dateTimeStamp';
import { Color } from '@/utils/constants';
import { isNaN } from 'lodash';

export function isDateTimeStamp(input: string): input is DateTimeStamp {
	return !isNaN(new Date(input));
}

export type SeriesData = { time: DateTimeStamp; value: number }[];

export type ProviderDataType = Provider['type'];

export const assetTypes = [
	'Cash',
	'Government Bonds',
	'Index Linked Bonds',
	'Corporate Bonds',
	'UK Equities',
	'Overseas Equities',
	'Listed Property',
	'Unlisted Property',
	'Listed Private Equity (inc VC)',
	'Unlisted Private Equity (inc VC)',
	'Listed Private Credit',
	'Unlisted Private Credit',
	'Listed Infrastructure',
	'Unlisted Infrastructure',
	'Other',
] as const;
export type AssetType = typeof assetTypes[number];
export type AssetAllocationData = Record<AssetType, number | null>;
export const assetTypeMetaData: Record<AssetType, { color: string; label: string }> = {
	Cash: { label: 'Cash', color: Color.YELLOW },
	'Government Bonds': { label: 'Government Bonds', color: '#283149' },
	'Index Linked Bonds': { label: 'Index Linked Bonds', color: '#58B2DC' },
	'Corporate Bonds': { label: 'Corporate Bonds', color: '#D72638' },
	'UK Equities': { label: 'UK Equities', color: '#3EC300' },
	'Overseas Equities': { label: 'Overseas Equities', color: '#EB328C' },
	'Listed Property': { label: 'Listed Property', color: '#F5A9B8' },
	'Unlisted Property': { label: 'Unlisted Property', color: '#CBAACB' },
	'Listed Private Equity (inc VC)': { label: 'Listed Private Equity (inc VC)', color: '#FF8C42' },
	'Unlisted Private Equity (inc VC)': { label: 'Unlisted Private Equity (inc VC)', color: '#D95D39' },
	'Listed Private Credit': { label: 'Listed Private Credit', color: '#8DAA91' },
	'Unlisted Private Credit': { label: 'Unlisted Private Credit', color: '#A9A9A9' },
	'Listed Infrastructure': { label: 'Listed Infrastructure', color: '#E9C46A' },
	'Unlisted Infrastructure': { label: 'Unlisted Infrastructure', color: '#8B5E3C' },
	Other: { label: 'Other', color: '#7B68EE' },
};

export const fundBracketTimes = ['30 Years', '5 Years', '1 Day'] as const;

export type FundBracketTime = typeof fundBracketTimes[number];
export const providerRatingLookbackTimes = ['1', '3', '5'] as const;
export type LookbackTime = typeof providerRatingLookbackTimes[number];

export type FundBracket = {
	percentageReturns: Record<LookbackTime, {
		percentageReturn?: number;
		annualisedReturn?: number;
		ranking?: number;
	}>;
	assetAllocationData: AssetAllocationData;
	fundBracketTime: FundBracketTime;
	fundValueData: SeriesData;
	topBottomPriceVariance: { year: string; value: number | null }[];
	topBottomPriceVarianceDataMap: Record<string, number | null>;

	annualisedRisk: Record<LookbackTime, number | null>;
	sharpeRatio: Record<LookbackTime, number | null>;
};

export const fundExclusionSectors = [
	'alcohol',
	'tobacco',
	'gambling',
	'pornography',
	'fossilFuels',
	'thermalCoalAndTarSands',
	'nuclearIndustry',
	'armaments',
	'controversialWeapons',
	'animalTesting',
	'furTrade',
	'globalCompactViolators',
] as const;
export type FundExclusionSector = typeof fundExclusionSectors[number];

export const fundExcludesionSectorsTitles: Record<FundExclusionSector, string> = {
	alcohol: 'Alcohol',
	tobacco: 'Tobacco',
	gambling: 'Gambling',
	pornography: 'Pornography',
	fossilFuels: 'Fossil Fuels',
	thermalCoalAndTarSands: 'Thermal Coal and Tar Sands',
	nuclearIndustry: 'Nuclear Industry',
	armaments: 'Armaments',
	controversialWeapons: 'Controversial Weapons',
	animalTesting: 'Animal Testing',
	furTrade: 'Fur Trade',
	globalCompactViolators: 'Global Compact Violators',
};

export const fundExlusionTypes = ['YES', 'NO', 'PARTIAL'] as const;
export type FundExclusionType = typeof fundExlusionTypes[number];

export type FullFund = {
	fundBrackets: FundBracket[];
	id: string;
	name: string;
	shortName: string | null;
	isCapaConstituent: boolean;
	color: string;
	tcfdWaci: number | null;
	fundExcludes: Record<FundExclusionSector, FundExclusionType | null>;
};

export type Fund = FullFund | FreeTierFund;

export type CapaFund<T extends Fund = Fund> = Omit<T, 'id'> & {
	id: 'CAPA';
};

export type FreeTierFundBracket = Pick<
	FundBracket,
	| 'fundBracketTime'
	| 'fundValueData'
	| 'percentageReturns'
>;

export type FreeTierFund =
	& Pick<
		FullFund,
		| 'id'
		| 'name'
		| 'shortName'
		| 'isCapaConstituent'
		| 'color'
	>
	& {
		fundBrackets: FreeTierFundBracket[];
	};

export type FreeTierProvider =
	& Pick<
		FullProvider,
		| 'id'
		| 'slug'
		| 'name'
		| 'imageUrl'
	>
	& {
		type: 'free';
		funds: FreeTierFund[];
	};

export type FullProvider = Omit<AdminProvider, 'members' | 'type'> & {
	type: 'full';
};

export type AdminProvider = {
	type: 'admin';
	id: string;
	isActive: boolean;
	imageUrl: string;
	name: string;
	shortName: string | null;
	slug: string;
	description: string;
	authorisedInPrivateMarket: boolean | null;
	technologyPlatformUsed: string | null;
	facilitatesAdviserCharging: string | null;
	facilitatesPensionsAdviceAllowance: string | null;
	appForAllMembers: boolean | null;
	percentageMembersDownloadedApp: number | null;
	percentageMembersViewedAppLastYear: number | null;
	percentageMembersNoimatedDeathBenefitBeneficiary: number | null;
	pensionStructuresOfferedReliefAtSource: boolean | null;
	pensionStructuresOfferedNetPay: boolean | null;
	offersPartialWithdrawlFromAge55: boolean | null;
	offersFlexiAccessDrawdown: boolean | null;
	offersUfpls: boolean | null;
	offersPartialUfpls: boolean | null;
	offersDripFeedDrawdown: boolean | null;
	dripdownFacilityAccommodatesProctedLifetimeAllowances: boolean | null;
	offersMutliGoalOrMultiWrapperBucketing: boolean | null;
	offersAnnuityBorkingService: string | null;
	tcfdReportLink: string | null;
	tcfdCarbonFootprint: number | null;
	tcfdOtherDecarbonisationMetrics: string | null;
	netZeroTargetYear: number | null;
	interimNetZeroTarget: string | null;
	createdAt: DateTimeStamp;

	activeDcMembersDataPoints: Record<number, number | null>;
	deferredDcMembersDataPoints: Record<number, number | null>;
	bundledDcAssetsDataPoints: Record<number, number | null>;
	employersAcrossDcBusinessDataPoints: Record<number, number | null>;
	bulkTransfersValueDataPoints: Record<number, number | null>;
	bulkTransfersNumberDataPoints: Record<number, number | null>;

	funds: FullFund[];
};

export type Provider = FreeTierProvider | FullProvider | AdminProvider;

export type FundWithProvider<T extends Provider = Provider> = T['funds'][number] & { provider: T };
